import {useContext, useEffect, useState} from "react";
import {ModalContext} from "../../../utils/modal/ModalProvider";
import styles from "../../../utils/modal/DetailModal.module.css";
import {IAM_SERVER_AUTH_TOKEN_POLICY, IAM_SERVER_SERVICE, IAM_SERVER_TENANT, IAM_SERVER_TENANT_USER} from "../../../utils/Config";
import axios from "../../../utils/api/axios";
import {DialogEnum, showDialog} from "../../../utils/dialog/Dialog";
import Loading from "../../../utils/loading/Loading";
import eye from "../../../resources/images/icon-font-eye.svg";
import eyeClose from "../../../resources/images/icon-font-eye-crossed.svg";
import { containsXss } from "../../../utils/security/XssFilter";
import OwnerTransferModal from "./OwnerTransferModal";
import { get } from "jquery";
import { AuthContext } from "../../../utils/contextapi/AuthProvider";
import { uploadProfileImage } from '../../../utils/api/storageApiClient';
import { InnocodeButton, useInnocodeLoadingOverlay } from "@intsoft/innocode-core";

/**
 * 사용자 상세정보 모달 Container
 * @param id
 * @param update
 * @returns {JSX.Element}
 * @constructor
 */
export const TenantDetailModal = ({id, tenantUserId, currentTenantType, update}) => {
    const { show: showLoading, hide: hideLoading } = useInnocodeLoadingOverlay();
    const {closeModal} = useContext(ModalContext);
    const [isNull, setIsNull] = useState(false); //빈 값 확인 
    const [showOwnerTransfer, setShowOwnerTransfer] = useState(false);
    const [businessNumberValid, setBusinessNumberValid] = useState(true);
    const {role} = useContext(AuthContext);
    const canManageTenant = String(currentTenantType || "").toLowerCase() === "owner";

    const [tenantLogoFile, setTenantLogoFile] = useState(null); //  로고 파일 객체를 저장할 상태
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null); //  로고 미리보기를 위한 상태

    const [formData, setFormData] = useState({
        tenantId: '',
        tenantName: '',
        detail: '',
        businessName: '',
        businessNumber: '',
        createdBy: '',
        createdByName: '',
        tenantImgUrl:'',
    });

    const fetchData = async () => {
        try {
            showLoading();
            const tenantRes = await axios.get(`${IAM_SERVER_TENANT + "/get/" + id}`);
            const tenantData = tenantRes.data.data;
            setFormData({
                tenantId: tenantData.tenantId,
                tenantName: tenantData.tenantName,
                detail: tenantData.detail,
                businessName:tenantData.businessName,
                businessNumber:tenantData.businessNumber,
                createdByName:tenantData.createdByName,
                createdBy:tenantData.createdBy,
                tenantImgUrl:tenantData.tenantImgUrl,
            });
            setLogoPreviewUrl(tenantData.tenantImgUrl);
        } catch (error) {
            showDialog(DialogEnum.DEFAULT, "실패", "사용자 정보 조회에 실패했습니다.");
        } finally{
            hideLoading();
        }
    }

    useEffect(() => {
        fetchData();
    }, []);

    const formatBusinessNumber = (numStr) => {
        if (!numStr || numStr.length !== 10) return numStr; // 이미 포맷된 값 또는 잘못된 길이
        return `${numStr.slice(0, 3)}-${numStr.slice(3, 5)}-${numStr.slice(5)}`;
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setTenantLogoFile(file); // 파일 객체 저장
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "businessNumber") {
            // 숫자만 추출
            const numeric = value.replace(/[^0-9]/g, "").slice(0, 10);

            // 하이픈 포맷 적용: XXX-XX-XXXXX
            let formatted = numeric;
            if (numeric.length >= 6) {
            formatted = `${numeric.slice(0, 3)}-${numeric.slice(3, 5)}-${numeric.slice(5)}`;
            } else if (numeric.length >= 4) {
            formatted = `${numeric.slice(0, 3)}-${numeric.slice(3)}`;
            }

            setFormData((prev) => ({
            ...prev,
            [name]: formatted
            }));

            // 유효성 체크
            setBusinessNumberValid(numeric.length === 10);
        } else {
            setFormData((prev) => ({
            ...prev,
            [name]: value
            }));
        }
    };


    // 사용자 상세정보 수정 요청 API
    const handleModify = async () => {

         let finalTenantImgUrl = formData.tenantImgUrl; // 기본값은 기존 URL

        try {
            // 1. 새로운 로고 파일이 있으면 업로드
            if (tenantLogoFile) {
                finalTenantImgUrl = await uploadProfileImage(
                    tenantLogoFile,
                    formData.createdByName, 
                    'zentry_tenant'   
                );
            }

            // 2. 업로드된 URL을 포함하여 수정 API 호출
            const params = {
                ...formData,
                tenantImgUrl: finalTenantImgUrl, 
            };
            
            const url = IAM_SERVER_TENANT + "/update";
            const res = await axios.post(url, params);

            showDialog(DialogEnum.SUCCESS, "성공", res.data.message, closeModal);
            update();

        } catch (e) {
            console.error("Tenant update failed:", e);
            showDialog(DialogEnum.DEFAULT, "실패", e.response.data.resultMessage);
        }
    };

    
    const handleDelete = () => {
        //테넌트 삭제는 테넌트 소유주만 가능하고 
        //테넌트 유저에 소유쥬이면서 오너인 유저가 단독으로 있을시에만 삭제 가능

        const canDeleteUrl = IAM_SERVER_TENANT + "/canDelete/" + formData.tenantId;

        showDialog(DialogEnum.CONFIRM, "테넌트 삭제 확인", `${formData.tenantName} 테넌트를 삭제 하시겠습니까?`, async () => {
        axios.get(canDeleteUrl)
            .then((res) => {

                if(res.data.code === 200){
                    axios.post(`${IAM_SERVER_TENANT + "/delete/" + formData.tenantId}`)
                        .then(async (res) => {
                            await axios.post(`${IAM_SERVER_TENANT_USER + "/delete/" + tenantUserId}`);
                            showDialog(DialogEnum.SUCCESS, "성공", "삭제 되었습니다.", closeModal);
                            update();
                        })
                        .catch((e) => {
                            showDialog(DialogEnum.DEFAULT, "실패", e.response.data.resultMessage);
                        })




                }else if(res.data.code === 400){
                    showDialog(DialogEnum.DEFAULT, "실패", res.data.message);
                }

            })
            .catch((e) => {
                showDialog(DialogEnum.DEFAULT, "실패", e.response.data.resultMessage);
            })
        });
    }

    const handleCreatedByChange = () => {
        //해당 테넌트의 오너 권한을 가진 유저를 조회 하여 리스트로 보여주고 소유주 전환 시 관리자에 승인이 필요 합니다. 라고 알럿이 필요 하고 
        //해당 테넌트의 오너 권한 유저를 보여줄때는 기본적으로 테넌트 유저에 연동된 프로필을 확인하고 프로필 정보에서 유저정보를 확인하여 
        //해당 유저의 아이디(email), 연결 프로필명 을 표시하여 리스트 노출 후 선택 할수 있게

        // 실제 승인이 되면 오너 권한 이전
        // 해당 기능은 신청까지만
        setShowOwnerTransfer(true);

    };

    return (
        <div style={{overflow: "auto"}}>
            <div>
                    <ul className={styles.layer__listWrap}>
                        <li className={styles.layer__list}>
                            <p className={styles.layer__listTit}>로고 이미지</p>
                            
                            {/* 오른쪽 영역을 감싸는 컨테이너 */}
                            <div className={styles.logoUploadContainer}>
                                {/* 1. 로고 미리보기 */}
                                <img
                                    src={logoPreviewUrl || "https://placehold.co/140x140?text=Logo"}
                                    alt="테넌트 로고"
                                    className={styles.logoPreviewSquare}
                                />
                                
                                {/* 2. 파일 선택 버튼과 설명 */}
                                <div className={styles.logoUploadControls}>
                                    <label htmlFor="tenantLogoUpload" className={styles.fileSelectButton}>
                                        파일 선택
                                    </label>
                                    <input
                                        id="tenantLogoUpload"
                                        type="file"
                                        accept=".jpg, .png, .gif" // ✅ 확장자 명시
                                        style={{ display: "none" }}
                                        onChange={handleLogoChange}
                                    />
                                    <p className={styles.logoUploadDescription}>
                                        테넌트를 대표할 수 있는 이미지로 설정해주세요.
                                        <br />
                                        권장 크기는 140X140 사이즈이며 500KB 이하의 jpg, png, gif만 등록 가능합니다.
                                    </p>
                                </div>
                            </div>
                        </li>
                        <li className={styles.layer__list}>
                            <p className={styles.layer__listTit}>
                                테넌트 명
                                {/* <span className={styles.tooltipIcon} title="테넌트 명은 수정할 수 없습니다.">⚠️</span> */}
                            </p>
                            <input className={styles.layer__listInput}
                                type={"text"}
                                name={"tenantName"}
                                value={formData.tenantName}
                                readOnly={true}
                                onChange={handleChange}/>
                        </li>
                         <li className={styles.layer__list}>
                            <p className={styles.layer__listTit}>
                                소유주
                                {/* <span className={styles.tooltipIcon} title="소유주는 수정할 수 없습니다.">⚠️</span> */}
                            </p>
                            <input className={styles.layer__listInput}
                                type={"text"}
                                name={"createdByName"}
                                value={formData.createdByName}
                                readOnly={true}
                                onChange={handleChange}/>

            { canManageTenant &&
                                <button
                                    type="button"
                                    className={styles.layer__listInput__duplicateCheckBtn}
                                    onClick={handleCreatedByChange}
                                >
                                    소유주 전환
                                </button>
                            }    
                            
                        </li>
                        <li className={styles.layer__list}>
                            <p className={styles.layer__listTit}>사업자 명</p>
                            <input className={styles.layer__listInput}
                                type={"text"}
                                name={"businessName"}
                                value={formData.businessName}
                                readOnly={true}
                                onChange={handleChange}/>
                        </li>
                        <li className={styles.layer__list}>
                            <p lang={"ko"} className={styles.layer__listTit}>사업자 번호</p>
                            <input className={styles.layer__listInput}
                                type={"text"}
                                name={"businessNumber"}
                                value={formatBusinessNumber(formData.businessNumber)}
                                readOnly={true}
                                onChange={handleChange}/>
                        </li>
                        {/* <li className={styles.layer__list}>
                            <p className={styles.layer__listTit}>설명</p>
                            <textarea
                                className={styles.layer__listInput}
                                name={"detail"}
                                value={formData.detail}
                                onChange={handleChange}
                                rows={4} // 원하는 줄 수 조절 가능
                            />
                        </li> */}
                    </ul>

            {isNull ? <div className={`${styles.layer__text} ${styles.warning}`}>빈 칸을 확인해 주세요.</div> : null}
            {!businessNumberValid && (<div className={`${styles.layer__text} ${styles.warning}`}>사업자 번호는 10자리 숫자여야 합니다.</div>)}
                    <div className={styles.layer__sensorBtnBox}>
                        {canManageTenant ? (
                            <>
                                <InnocodeButton
                                    backgroundColor=' var(--status-negative, #f83a3a)'
                                    onClick={handleDelete}
                                    className={styles.layer__actionBtn}
                                    fullWidth={true}
                                >
                                    삭제
                                </InnocodeButton>
                                <InnocodeButton
                                    onClick={handleModify}
                                    className={styles.layer__actionBtn}
                                    fullWidth={true}
                                >
                                    수정
                                </InnocodeButton>
                            </>
                        ) : (
                            <InnocodeButton
                                onClick={closeModal}
                                className={styles.layer__actionBtn}
                                fullWidth={true}
                            >
                                확인
                            </InnocodeButton>
                        )}
                        {/* <button className={styles.layer__cancleBtn} onClick={handleDelete}>삭제</button>
                        <button className={styles.layer__modifyBtn} onClick={handleModify}>수정</button>                         */}
                    </div>                    
                </div>

            {showOwnerTransfer && (
            <OwnerTransferModal
                tenantId={formData.tenantId}
                tenantName={formData.tenantName}
                tenantUserId={tenantUserId}
                onClose={() => setShowOwnerTransfer(false)}
            />
            )}
        </div>
    );
}
